import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';
import { formatDisplayDate } from '@/utils/date';
import { toast } from 'sonner';
import { Plus, DollarSign, Calendar, Tag, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import LogoLoader from '@/components/LogoLoader';

const Expenses = () => {
  const { token } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [projects, setProjects] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [filterProject, setFilterProject] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    project_id: '',
    po_id: '',
    expense_type: 'Operational',
    expense_date: '',
    vendor: '',
    category: '',
    description: '',
    amount: '',
    currency: 'USD',
    billable_flag: false,
    pass_through_flag: false,
    markup_pct: '0',
    direct_relation_type: 'Not Applicable'
  });

  const expenseTypes = [
    'Operational',      // General business operations (rent, utilities, salaries)
    'Direct',          // Directly tied to revenue (project costs that can be billed)
    'Indirect',        // Overhead costs (admin, office supplies)
    'Project-specific' // Specific project expense
  ];

  const directRelationOptions = [
    'Project',
    'Purchase Order',
    'Not Applicable'
  ];

  const expenseCategories = [
    'Rent & Lease',
    'Utilities',
    'Salaries & Wages',
    'Travel',
    'Meals & Entertainment',
    'Software & Tools',
    'Hardware',
    'Office Supplies',
    'Training & Certification',
    'Consulting Services',
    'Legal & Professional',
    'Marketing & Advertising',
    'Insurance',
    'Taxes & Licenses',
    'Other'
  ];

  useEffect(() => {
    loadData();
  }, [filterProject, filterType]);

  const loadData = async () => {
    setLoading(true);
    try {
      let expensesUrl = '/expenses';
      const params = new URLSearchParams();
      if (filterProject !== 'all') params.append('project_id', filterProject);
      if (filterType !== 'all') params.append('expense_type', filterType);
      if (params.toString()) expensesUrl += '?' + params.toString();

      const [projectsData, poData, expensesData] = await Promise.all([
        api.get('/projects', token),
        api.get('/purchase-orders', token),
        api.get(expensesUrl, token)
      ]);

      setProjects(projectsData);
      setPurchaseOrders(poData);
      setExpenses(expensesData);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleDirectRelationChange = (value) => {
    setFormData((prev) => {
      if (value === 'Project') {
        return {
          ...prev,
          direct_relation_type: value,
          po_id: ''
        };
      }
      if (value === 'Purchase Order') {
        return {
          ...prev,
          direct_relation_type: value,
          project_id: ''
        };
      }
      return {
        ...prev,
        direct_relation_type: value,
        project_id: '',
        po_id: ''
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const isDirect = formData.expense_type === 'Direct';
      const payload = {
        ...formData,
        project_id: isDirect
          ? (formData.direct_relation_type === 'Project' ? formData.project_id || null : null)
          : (formData.project_id || null),
        po_id: isDirect && formData.direct_relation_type === 'Purchase Order'
          ? (formData.po_id || null)
          : null,
        direct_relation_type: isDirect ? formData.direct_relation_type : null,
        vendor: formData.vendor || null,
        category: formData.category || null,
        amount: parseFloat(formData.amount),
        markup_pct: parseFloat(formData.markup_pct)
      };
      await api.post('/expenses', payload, token);
      toast.success('Expense created successfully');
      setShowDialog(false);
      loadData();
      setFormData({
        project_id: '',
        po_id: '',
        expense_type: 'Operational',
        expense_date: '',
        vendor: '',
        category: '',
        description: '',
        amount: '',
        currency: 'USD',
        billable_flag: false,
        pass_through_flag: false,
        markup_pct: '0',
        direct_relation_type: 'Not Applicable'
      });
    } catch (error) {
      toast.error('Failed to create expense');
    }
  };

  // Calculate totals
  const totalExpenses = expenses.reduce((sum, exp) => sum + exp.amount, 0);
  const billableExpenses = expenses.filter(e => e.billable_flag).reduce((sum, e) => sum + e.amount, 0);
  const nonBillableExpenses = totalExpenses - billableExpenses;

  if (loading) {
    return <LogoLoader message="Loading expenses..." />;
  }

  return (
    <div className="space-y-6" data-testid="expenses-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-utis-forest">Expenses</h1>
          <p className="text-gray-600 mt-1">Track project expenses and billable costs</p>
        </div>
        <Button 
          onClick={() => setShowDialog(true)} 
          className="bg-utis-forest hover:bg-utis-forest/90 text-white"
          data-testid="add-expense-button"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Expense
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="border-utis-sage">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Expenses</p>
                <p className="text-3xl font-bold text-utis-forest mt-2">
                  ${totalExpenses.toLocaleString()}
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-utis-sage/20 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-utis-forest" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-green-200 bg-green-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-900">Billable Expenses</p>
                <p className="text-3xl font-bold text-green-900 mt-2">
                  ${billableExpenses.toLocaleString()}
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-green-200 flex items-center justify-center">
                <Tag className="w-6 h-6 text-green-700" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-gray-200 bg-gray-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Non-Billable</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">
                  ${nonBillableExpenses.toLocaleString()}
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center">
                <Building2 className="w-6 h-6 text-gray-700" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Expense List</CardTitle>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <Label>Type:</Label>
                <Select value={filterType} onValueChange={setFilterType}>
                  <SelectTrigger className="w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    {expenseTypes.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center space-x-2">
                <Label>Project:</Label>
                <Select value={filterProject} onValueChange={setFilterProject}>
                  <SelectTrigger className="w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Projects</SelectItem>
                    {projects.filter(p => p.is_active).map(project => (
                      <SelectItem key={project.id} value={project.id}>
                        {project.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {expenses.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              No expenses yet. Click "Add Expense" to get started.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Expense Type</TableHead>
                  <TableHead>Linked To</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Billable</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {expenses.map((expense) => {
                  const project = projects.find(p => p.id === expense.project_id);
                  const purchaseOrder = purchaseOrders.find(po => po.id === expense.po_id);
                  return (
                    <TableRow key={expense.id}>
                      <TableCell>
                        {formatDisplayDate(expense.expense_date, '-')}
                      </TableCell>
                      <TableCell>
                        <Badge 
                          variant="outline" 
                          className={
                            expense.expense_type === 'Direct' ? 'bg-green-50 text-green-800' :
                            expense.expense_type === 'Operational' ? 'bg-blue-50 text-blue-800' :
                            expense.expense_type === 'Project-specific' ? 'bg-purple-50 text-purple-800' :
                            'bg-gray-50 text-gray-800'
                          }
                        >
                          {expense.expense_type}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-medium">
                        {expense.expense_type === 'Direct' ? (
                          <div className="space-y-1">
                            <Badge variant="outline" className="w-fit">
                              {expense.direct_relation_type || 'Not Applicable'}
                            </Badge>
                            {expense.direct_relation_type === 'Project' && (
                              <span className="block text-sm text-gray-700">
                                {project?.name || <span className="text-gray-400 italic">Project unavailable</span>}
                              </span>
                            )}
                            {expense.direct_relation_type === 'Purchase Order' && (
                              <span className="block text-sm text-gray-700">
                                {purchaseOrder ? `PO ${purchaseOrder.po_number}` : <span className="text-gray-400 italic">Purchase order unavailable</span>}
                              </span>
                            )}
                            {(!expense.direct_relation_type || expense.direct_relation_type === 'Not Applicable') && (
                              <span className="block text-sm text-gray-500">Not linked</span>
                            )}
                          </div>
                        ) : (
                          project?.name || <span className="text-gray-400 italic">No project</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{expense.category || 'Uncategorized'}</Badge>
                      </TableCell>
                      <TableCell>{expense.vendor || '-'}</TableCell>
                      <TableCell className="max-w-xs truncate">{expense.description}</TableCell>
                      <TableCell className="font-bold">
                        ${expense.amount.toLocaleString()}
                        {expense.markup_pct > 0 && (
                          <span className="text-xs text-gray-500 ml-1">
                            (+{expense.markup_pct}%)
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        {expense.billable_flag ? (
                          <Badge className="bg-green-100 text-green-800">Billable</Badge>
                        ) : (
                          <Badge variant="secondary">Non-Billable</Badge>
                        )}
                        {expense.pass_through_flag && (
                          <Badge variant="outline" className="ml-1 text-xs">Pass-through</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add Expense Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Expense</DialogTitle>
            <DialogDescription>Record a business expense - operational, direct, indirect, or project-specific</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Expense Type *</Label>
                <Select
                  value={formData.expense_type}
                  onValueChange={(val) => {
                    setFormData((prev) => ({
                      ...prev,
                      expense_type: val,
                      direct_relation_type: val === 'Direct' ? prev.direct_relation_type : 'Not Applicable',
                      po_id: val === 'Direct' ? prev.po_id : '',
                      project_id: val === 'Direct' ? '' : prev.project_id
                    }));
                  }}
                  required
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {expenseTypes.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">
                  {formData.expense_type === 'Operational' && 'General business operations (rent, utilities, salaries)'}
                  {formData.expense_type === 'Direct' && 'Directly tied to revenue (billable costs)'}
                  {formData.expense_type === 'Indirect' && 'Overhead costs (admin, office)'}
                  {formData.expense_type === 'Project-specific' && 'Specific project expense'}
                </p>
              </div>
              <div>
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={formData.expense_date}
                  onChange={(e) => setFormData({...formData, expense_date: e.target.value})}
                  required
                />
              </div>
              {formData.expense_type === 'Direct' ? (
                <>
                  <div>
                    <Label>Direct Expense Linked To *</Label>
                    <Select
                      value={formData.direct_relation_type}
                      onValueChange={handleDirectRelationChange}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {directRelationOptions.map(option => (
                          <SelectItem key={option} value={option}>{option}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {formData.direct_relation_type === 'Project' && (
                    <div>
                      <Label>Project *</Label>
                      <Select
                        value={formData.project_id || ''}
                        onValueChange={(val) => setFormData({...formData, project_id: val})}
                        required
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select project" />
                        </SelectTrigger>
                        <SelectContent>
                          {projects.filter(p => p.is_active).map(project => (
                            <SelectItem key={project.id} value={project.id}>
                              {project.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {formData.direct_relation_type === 'Purchase Order' && (
                    <div>
                      <Label>Purchase Order *</Label>
                      <Select
                        value={formData.po_id || ''}
                        onValueChange={(val) => setFormData({...formData, po_id: val})}
                        required
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select purchase order" />
                        </SelectTrigger>
                        <SelectContent>
                          {purchaseOrders.filter(po => po.is_active).map(po => (
                            <SelectItem key={po.id} value={po.id}>
                              {po.po_number}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {formData.direct_relation_type === 'Not Applicable' && (
                    <div className="md:col-span-2">
                      <p className="text-xs text-gray-500 mt-6">
                        This direct expense will be recorded without linking to a specific project or purchase order.
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <div className="md:col-span-2">
                  <Label>Project {formData.expense_type === 'Project-specific' ? '*' : '(Optional)'}</Label>
                  <Select
                    value={formData.project_id || "none"}
                    onValueChange={(val) => setFormData({...formData, project_id: val === "none" ? "" : val})}
                    required={formData.expense_type === 'Project-specific'}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select project (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No project</SelectItem>
                      {projects.filter(p => p.is_active).map(project => (
                        <SelectItem key={project.id} value={project.id}>
                          {project.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <Select 
                  value={formData.category} 
                  onValueChange={(val) => setFormData({...formData, category: val})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {expenseCategories.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Vendor</Label>
                <Input
                  value={formData.vendor}
                  onChange={(e) => setFormData({...formData, vendor: e.target.value})}
                  placeholder="e.g., AWS, Microsoft"
                />
              </div>
            </div>

            <div>
              <Label>Description *</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                required
                placeholder="Describe the expense"
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Amount *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({...formData, amount: e.target.value})}
                  required
                  placeholder="0.00"
                />
              </div>
              <div>
                <Label>Markup %</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.markup_pct}
                  onChange={(e) => setFormData({...formData, markup_pct: e.target.value})}
                  placeholder="0.0"
                />
                <p className="text-xs text-gray-500 mt-1">For billable expenses only</p>
              </div>
            </div>

            <div className="border-t pt-4 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="font-medium">Billable to Client</Label>
                  <p className="text-xs text-gray-500">Include in client invoices</p>
                </div>
                <Switch
                  checked={formData.billable_flag}
                  onCheckedChange={(checked) => setFormData({...formData, billable_flag: checked})}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label className="font-medium">Pass-Through Expense</Label>
                  <p className="text-xs text-gray-500">Client reimburses at cost (no markup)</p>
                </div>
                <Switch
                  checked={formData.pass_through_flag}
                  onCheckedChange={(checked) => setFormData({...formData, pass_through_flag: checked})}
                />
              </div>
            </div>

            <Button type="submit" className="w-full bg-utis-forest hover:bg-utis-forest/90 text-white">
              Create Expense
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Expenses;
